import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleOnSettled } from '@utils/formHelpers';
import { useUpdateMaterial } from '@services/materials/update';
import { DefaultApiResp } from '@root/globalTypes';
import { useErrors } from '@hooks/errors';
import { setFinalAndEstimatedValuesForFinalYard } from '@components/job/materials/helpers';
import { FormProps } from '@forms/types';
import { MaterialMeasurementFormProps, MaterialMeasurementFormValues } from './types';
import { defaultValues, formSchema } from './constants';

export const useMaterialMeasurementForm = ({
  successHandler,
  material,
}: FormProps<MaterialMeasurementFormProps>) => {
  const handleErrors = useErrors();

  const form = useForm<MaterialMeasurementFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { control, setError } = form;

  const fieldArray = useFieldArray({ control, name: 'optionsAttributes' });

  const callback = (resp: DefaultApiResp) => {
    handleOnSettled<DefaultApiResp>(resp, successHandler, () =>
      handleErrors(resp.errors, (key, message) =>
        setError(key as keyof MaterialMeasurementFormValues, { message }),
      ),
    );
  };

  const update = useUpdateMaterial({ callback });

  const onSubmit = async ({ optionsAttributes }: MaterialMeasurementFormValues) => {
    if (!material) return;

    const { id, jobId } = material;
    const isFinal = document.querySelector('[name="final"]') as HTMLInputElement;

    const data = {
      id,
      jobId,
      optionsAttributes: optionsAttributes?.map((option) => {
        const finalValue = setFinalAndEstimatedValuesForFinalYard(option, isFinal?.checked);
        return {
          ...{ id: option.id },
          ...finalValue,
        };
      }),
    };

    update.mutate(data);
  };

  return {
    form,
    fieldArray,
    onSubmit,
    locked: update.isLoading,
  };
};
